import * as Tabs from '@radix-ui/react-tabs';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router';
import type { Attachment, BoardColumn, ClientStatus, Message } from '@rundown/types';
import { CONTROL_CLASS, EmptyState, LoadMore, ResizeHandle } from '../../components';
import { useStoredNumber } from '../../lib/useStoredNumber';
import { formatDateTime, formatHours } from '../../lib/format';
import { SIZE_LABELS, sizeFromHours } from '@rundown/domain';
import { avatarColour, initialOf } from '../../lib/avatar';
import { cx } from '../../lib/cx';
import { queryKeys } from '../../lib/queryKeys';
import { useAuth } from '../auth/useAuth';
import { useCapabilities, useCapability } from '../workspace/useCapability';
import { useRequiredWorkspace, useSizeThresholds } from '../workspace/useWorkspace';
import { isInternalRequest, requestQueryOptions, type RequestRow } from './api';
import {
  deleteMessage,
  editMessage,
  messagesInfiniteQueryOptions,
  postMessage,
  type NewMessageInput,
} from './messagesApi';
import {
  ConfirmDelete,
  EditedMark,
  MessageActions,
  MessageEditor,
  useMayChange,
} from '../threads/MessageControls';
import { AttachmentStrip, PendingStrip } from '../attachments/AttachmentStrip';
import { attachmentsQueryOptions } from '../attachments/api';
import { useUploader } from '../attachments/useUploader';
import { Composer, type ComposerHandle } from '../composer/Composer';
import { MessageBodyView } from '../composer/MessageBodyView';
import { inlineAttachmentIds } from '../composer/imageNode';
import { RequestTimeLog } from '../board/RequestTimeLog';
import { timeEntriesQueryOptions } from '../board/api';
import { useSetColumn } from '../board/useSetColumn';
import { RequestActivityTab } from '../activity/RequestActivityTab';
import { RequestIntakeSection } from '../intake/RequestIntakeSection';
import { RequestFields } from './RequestFields';
import { RequestActions } from './RequestActions';
import { FollowToggle } from './FollowToggle';
import { ReviewBanner } from './ReviewBanner';
import { useUpdateRequest } from './useRequestMutations';
import { BotMessage } from '../threads/BotMessage';
import { ActionContextMenu, type MenuAction } from '../threads/ThreadMenu';
import { Copy, Pencil, Trash2 } from 'lucide-react';
import {
  BOARD_COLUMNS,
  COLUMN_LABELS,
  PRIORITY_LABELS,
  STATUS_LABELS,
  STATUS_TONES,
  TYPE_LABELS,
  TYPE_TONES,
} from './labels';

/**
 * The Request screen — spec §4's core screen, and the only place both sides meet.
 *
 * Shape follows the design prototype: a fixed-height pane that never scrolls as
 * a page. The conversation is the wider column with its own scroll and a pinned
 * composer; the narrower left column scrolls independently and carries the
 * fields, progress and description.
 *
 * Why fixed-height rather than a long scrolling page: the composer has to stay
 * reachable while you read back through the thread. If the page scrolls as one,
 * replying to something near the top means scrolling to the bottom first.
 */

/**
 * How much of the request screen the details column takes, as a percentage.
 * The default is the old fixed 1 : 1.15 split.
 */
const SPLIT_DEFAULT = 46.5;
const SPLIT_MIN = 28;
const SPLIT_MAX = 70;
const clampSplit = (value: number) => Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, value));

/** The client's six states, in order. Progress is read from position in this list. */
const STATUS_ORDER: readonly ClientStatus[] = [
  'received',
  'queued',
  'scheduled',
  'in_progress',
  'testing',
  'released',
];

const TONE_TEXT: Record<string, string> = {
  neutral: 'text-muted',
  accent: 'text-accent',
  success: 'text-success',
  warn: 'text-warn',
  danger: 'text-danger',
  violet: 'text-violet',
};

const TONE_CHIP: Record<string, string> = {
  neutral: 'bg-surface-3 text-muted',
  accent: 'bg-accent-tint text-accent',
  success: 'bg-success-tint text-success',
  warn: 'bg-warn-tint text-warn',
  danger: 'bg-danger-tint text-danger',
  violet: 'bg-violet-tint text-violet',
};

function Chip({ tone, children }: { tone: string; children: React.ReactNode }) {
  return (
    <span
      className={cx(
        'flex-none rounded-full px-2 py-0.5 fs-11 font-semibold whitespace-nowrap',
        TONE_CHIP[tone] ?? TONE_CHIP['neutral'],
      )}
    >
      {children}
    </span>
  );
}

/**
 * The title.
 *
 * There were two — a client-facing one and a private `internalTitle` Noorix
 * kept its own phrasing in — and the second is gone. It was a second field to
 * write on every request, a second line to read on every card, and a standing
 * risk that the private one leaked through a query that forgot to project it
 * away. A request whose real name cannot be shown to the client is a signal
 * about the request, not a reason to carry two names for it.
 */
function TitleBlock({
  workspaceId,
  request,
  canEdit,
}: {
  workspaceId: string;
  request: RequestRow;
  canEdit: boolean;
}) {
  const update = useUpdateRequest(workspaceId, request.id);

  if (!canEdit) {
    return (
      <h1 className="flex-none fs-19 leading-tight font-bold tracking-[-0.01em] text-ink">{request.title}</h1>
    );
  }

  return (
    <div className="flex-none">
      <InlineText
        value={request.title}
        ariaLabel="Title"
        className="w-full border-0 border-b border-transparent bg-transparent p-0 pb-[0.1875rem] fs-19 leading-tight font-bold tracking-[-0.01em] text-ink focus:border-line-strong focus:outline-none"
        onCommit={(title) => {
          if (title.trim().length < 3) return;
          update.mutate({ title: title.trim() });
        }}
      />
      {update.error !== null && (
        <p role="alert" className="mt-1 fs-11 text-danger">
          {update.error.message}
        </p>
      )}
    </div>
  );
}

/**
 * An input that commits on blur and on Enter, and abandons on Escape.
 *
 * Saving per keystroke would put an activity row in the trail for every
 * character typed. The draft is only re-seeded from the server while the field
 * is not focused, so a realtime update cannot overwrite what someone is midway
 * through typing.
 */
function InlineText({
  value,
  ariaLabel,
  placeholder,
  className,
  onCommit,
}: {
  value: string;
  ariaLabel: string;
  placeholder?: string;
  className: string;
  onCommit: (next: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  return (
    <input
      value={draft}
      aria-label={ariaLabel}
      placeholder={placeholder}
      className={className}
      onFocus={() => setEditing(true)}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => {
        setEditing(false);
        if (draft !== value) onCommit(draft);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        }
        if (event.key === 'Escape') {
          setDraft(value);
          setEditing(false);
          event.currentTarget.blur();
        }
      }}
    />
  );
}

/**
 * A long description, clamped.
 *
 * Intake writes the client's own words followed by every question it asked and
 * the answer given, so a request that took a few rounds arrives several screens
 * tall. Unclamped, that pushed the fields, the time log and everything below
 * so far down the column that the left pane became a scrollbar down the middle
 * of the screen.
 *
 * Collapsed to a fixed height with the last line fading out, so it is visibly
 * cut rather than ambiguously ended. Measured rather than assumed: the button
 * only appears when the content actually overflows, so a two-line description
 * is not decorated with a "Show more" that does nothing.
 *
 * Takes children rather than a string, because a description is a rendered
 * document now — paragraphs, lists, code blocks — and measuring it means
 * measuring the element, which works the same either way.
 */
function ClampedDescription({ children }: { children: ReactNode }) {
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  // A callback ref, not useEffect + useRef: this has to measure the node the
  // moment it exists, and again whenever the content changes — a save
  // re-renders with a different tree, and the ref callback runs again for it.
  const measure = useCallback((node: HTMLDivElement | null) => {
    if (node === null) return;
    setOverflows(node.scrollHeight > node.clientHeight + 1);
  }, []);

  return (
    <>
      <div className="relative">
        <div
          ref={expanded ? null : measure}
          className={cx('fs-13 leading-relaxed text-ink-soft', !expanded && 'max-h-[16rem] overflow-hidden')}
        >
          {children}
        </div>
        {!expanded && overflows && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-base to-transparent"
          />
        )}
      </div>
      {overflows && (
        <button
          type="button"
          onClick={() => setExpanded((current) => !current)}
          className="mt-1 fs-12 font-semibold text-accent hover:underline"
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </>
  );
}

/**
 * Description — rich text, with an explicit Edit / Save.
 *
 * Explicit rather than commit-on-blur because this is prose people write in
 * several passes, and a blur that saves records a half-written paragraph in
 * the trail for everyone following.
 *
 * It was a plain textarea until now, which is why pasting a log or a JSON blob
 * into a bug report produced an unreadable run of text. It is the same editor
 * the message composer uses, sharing one extension set and validated by the
 * same parser on the server — so a pasted code block, a list, or a link
 * survives the round trip.
 *
 * ── Reading a description written before this ───────────────────────────────
 * Every existing row has `description` null and its words in
 * `descriptionText`, and intake still writes plain text that way. The renderer
 * takes both: `fallbackText` is what `MessageBodyView` shows when the document
 * is empty or absent, so nothing needs migrating and intake needs no change.
 * The clamp applies to either.
 */
function Description({
  workspaceId,
  request,
  canEdit,
  bare = false,
}: {
  workspaceId: string;
  request: RequestRow;
  canEdit: boolean;
  /**
   * In its own tab: no heading (the tab says it) and no "show more" clamp —
   * the tab exists so the description can be read in full, and scrolled.
   */
  bare?: boolean;
}) {
  const update = useUpdateRequest(workspaceId, request.id);
  const [editing, setEditing] = useState(false);
  const editorRef = useRef<ComposerHandle>(null);

  // Pasted and attached files hang off the REQUEST, not a message — a
  // screenshot in the description belongs to the request itself, and shows in
  // Attachments when it is not inline.
  const uploader = useUploader(workspaceId, { requestId: request.id });
  const attachmentsQuery = useQuery(attachmentsQueryOptions(workspaceId, request.id));
  // Which of this session's uploads went into the text rather than the strip.
  const [inlineIds, setInlineIds] = useState<ReadonlySet<string>>(new Set());
  const urlFor = (attachmentId: string) =>
    (attachmentsQuery.data?.items ?? []).find((item) => item.id === attachmentId)?.url;

  const save = () => {
    const description = editorRef.current?.getContent();
    if (description === undefined || description === null) return;
    // The editor's own `getContent` has already removed the transient blob
    // URLs and any image whose upload did not land — see RichTextEditor.
    update.mutate(
      { description },
      {
        onSuccess: () => {
          setEditing(false);
          uploader.clear();
        },
      },
    );
  };

  const hasContent = request.descriptionText.trim() !== '';

  return (
    <section>
      <div className={cx('flex items-baseline justify-between gap-2.5', bare ? 'mb-1' : 'mb-1.5')}>
        {bare ? (
          <span />
        ) : (
          <div className="fs-11 font-bold tracking-[0.06em] text-muted uppercase">Description</div>
        )}
        {canEdit && !editing && (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="fs-12 whitespace-nowrap text-accent"
          >
            Edit
          </button>
        )}
      </div>

      {editing ? (
        <>
          {/* The same editor the thread and the ask box use — one
              implementation, one node set, one paste-and-drop behaviour. The
              toolbar is the only thing that differs, because a description is
              written and formatted while a reply is usually a sentence. */}
          <Composer
            ref={editorRef}
            workspaceId={workspaceId}
            ariaLabel="Description"
            placeholder="What is happening, and what should happen instead?"
            toolbar
            // The stored document when there is one; otherwise the plain text
            // a textarea or intake left behind, which Tiptap parses into
            // paragraphs — so editing an old description does not start blank.
            initialContent={request.description ?? request.descriptionText}
            onPasteFiles={(event) => uploader.handlePaste(event)}
            onUpload={async (files, destination) => {
              const landed = await uploader.addFiles(files);
              if (destination === 'inline') {
                setInlineIds((current) => new Set([...current, ...landed.map((item) => item.id)]));
              }
              // The strip and any inline node both resolve URLs from this
              // list, so it has to include what was just uploaded.
              await attachmentsQuery.refetch();
              return landed;
            }}
          />
          {/* Minus the images already showing in the text: an inline one that
              has landed is visible where it was put, and listing it here as
              well shows the same screenshot twice. Still shown while it is
              uploading or if it failed, which is the only feedback there is
              for an image that never arrives. */}
          <PendingStrip
            uploads={uploader.uploads.filter(
              (upload) =>
                !(
                  upload.status === 'done' &&
                  upload.attachment !== undefined &&
                  inlineIds.has(upload.attachment.id)
                ),
            )}
            onRemove={uploader.remove}
          />
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="flex-1 fs-11 text-faint">Everyone following the request sees this change.</span>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-control border border-line bg-base px-3 py-1.5 fs-12 whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={update.isPending}
              onClick={save}
              className="rounded-control bg-ink px-3 py-1.5 fs-12 font-semibold whitespace-nowrap text-white disabled:opacity-50"
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </>
      ) : hasContent ? (
        bare ? (
          <div className="fs-14 leading-relaxed">
            <MessageBodyView
              body={request.description}
              fallbackText={request.descriptionText}
              resolveAttachmentUrl={urlFor}
            />
          </div>
        ) : (
          <ClampedDescription>
            <MessageBodyView
              body={request.description}
              fallbackText={request.descriptionText}
              resolveAttachmentUrl={urlFor}
            />
          </ClampedDescription>
        )
      ) : (
        <p className="fs-13 text-faint">No description was given.</p>
      )}

      {update.error !== null && (
        <p role="alert" className="mt-2 fs-11 text-danger">
          {update.error.message}
        </p>
      )}
    </section>
  );
}

function Progress({ status }: { status: ClientStatus }) {
  const reached = STATUS_ORDER.indexOf(status);
  return (
    <div className="flex gap-1">
      {STATUS_ORDER.map((step, index) => (
        <div
          key={step}
          title={STATUS_LABELS[step]}
          className={cx('h-1 flex-1 rounded-sm', index <= reached ? 'bg-ink' : 'bg-surface-4')}
        />
      ))}
    </div>
  );
}

/**
 * The one control on this screen that moves a request along, directly under the
 * word it moves.
 *
 * It used to sit in the Fields panel labelled "Board column", which made this
 * screen state where the work had got to three times over — the chip at the
 * top, the track this now sits under, and a select naming the same six values
 * in a fourth vocabulary. The column is not redundant DATA: the client's status
 * is derived from it and the sprint together (`clientStatusOf`), and a column
 * alone cannot tell Queued from Scheduled. Three places saying it were.
 *
 * `board.write`, not `request.review`. The server gates the column on that
 * capability by itself, so anything looser here would only be offering an edit
 * the server will refuse.
 *
 * Writes through `useSetColumn` — the board move — rather than the request
 * PATCH, which is the same call the status menu on every list makes. One way to
 * move a card: it computes the card's rank in its new column, writes the
 * `request.board_moved` trail entry rather than a generic field change, and
 * notifies the watchers. A second path that did two of those three is how the
 * trail ends up describing the same act two ways.
 */
function DeliveryColumn({
  workspaceId,
  requestId,
  column,
}: {
  workspaceId: string;
  requestId: string;
  column: BoardColumn;
}) {
  const setColumn = useSetColumn(workspaceId);

  return (
    <label className="flex min-w-0 flex-col gap-1">
      <span
        className="fs-11 text-muted"
        title="Internal. What the client sees follows from this and the sprint."
      >
        Delivery column
      </span>
      <select
        value={column}
        disabled={setColumn.isPending}
        onChange={(event) => setColumn.mutate({ requestId, column: event.target.value as BoardColumn })}
        className={CONTROL_CLASS}
      >
        {BOARD_COLUMNS.map((option) => (
          <option key={option} value={option}>
            {COLUMN_LABELS[option]}
          </option>
        ))}
      </select>
      {setColumn.error !== null && (
        <span role="alert" className="fs-11 text-danger">
          {setColumn.error.message}
        </span>
      )}
    </label>
  );
}

type LeftTab = 'description' | 'details' | 'time';

/**
 * The request at a glance, in one flowing line under the title: where it has
 * got to, how urgent, how big, whose. Each fact opens the tab where it is
 * changed, so the summary is also the way in to editing it.
 */
function RequestSummary({
  request,
  loggedHours,
  onOpen,
}: {
  request: RequestRow;
  loggedHours: number | null;
  onOpen: (tab: LeftTab) => void;
}) {
  const thresholds = useSizeThresholds();
  const size = sizeFromHours(request.approxHours, thresholds);
  const reached = STATUS_ORDER.indexOf(request.status);

  const fact = (key: string, label: string, value: ReactNode, tab: LeftTab) => (
    <button
      key={key}
      type="button"
      onClick={() => onOpen(tab)}
      className="flex min-w-0 flex-col items-start rounded-md px-2 py-1 text-left hover:bg-surface-2"
    >
      <span className="fs-11 text-faint">{label}</span>
      <span className="max-w-[12rem] truncate fs-12.5 font-medium text-ink">{value}</span>
    </button>
  );

  return (
    <div className="-mx-2 mb-3 flex flex-none flex-wrap items-stretch gap-x-1 gap-y-1">
      {fact(
        'status',
        'Status',
        <span className="flex items-center gap-2">
          <span className={TONE_TEXT[STATUS_TONES[request.status]] ?? 'text-ink'}>
            {STATUS_LABELS[request.status]}
          </span>
          <span className="flex gap-0.5" aria-hidden>
            {STATUS_ORDER.map((step, index) => (
              <span
                key={step}
                className={cx('h-1 w-2.5 rounded-sm', index <= reached ? 'bg-ink' : 'bg-surface-4')}
              />
            ))}
          </span>
        </span>,
        'details',
      )}
      {fact('priority', 'Priority', PRIORITY_LABELS[request.priority], 'details')}
      {fact(
        'estimate',
        'Estimate',
        request.approxHours > 0 ? `${SIZE_LABELS[size]} · ~${formatHours(request.approxHours)}` : 'Not sized',
        'details',
      )}
      {fact('project', 'Project', request.projectName ?? 'None', 'details')}
      {fact('owner', 'Owner', request.ownerName ?? 'Unassigned', 'details')}
      {loggedHours !== null && fact('logged', 'Logged', formatHours(loggedHours), 'time')}
    </div>
  );
}

export function MessageRow({
  workspaceId,
  message,
  attachments,
  resolveAttachmentUrl,
}: {
  workspaceId: string;
  message: Message;
  attachments: readonly Attachment[];
  /** Inline images in the body store an attachment id, not a URL. */
  resolveAttachmentUrl: (attachmentId: string) => string | undefined;
}) {
  // @rundown's messages carry meta the plain row cannot draw — the question
  // card, the "filed as" chips — so they get their own component.
  if (message.authorKind === 'bot') return <BotMessage workspaceId={workspaceId} message={message} />;
  return (
    <PersonMessage
      workspaceId={workspaceId}
      message={message}
      attachments={attachments}
      resolveAttachmentUrl={resolveAttachmentUrl}
    />
  );
}

function PersonMessage({
  workspaceId,
  message,
  attachments,
  resolveAttachmentUrl,
}: {
  workspaceId: string;
  message: Message;
  attachments: readonly Attachment[];
  resolveAttachmentUrl: (attachmentId: string) => string | undefined;
}) {
  const queryClient = useQueryClient();
  const mayChange = useMayChange(message.authorId);
  const [mode, setMode] = useState<'read' | 'edit' | 'confirm-delete'>('read');
  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: queryKeys.requestMessages(workspaceId, message.requestId),
    });
    void queryClient.invalidateQueries({ queryKey: queryKeys.threads(workspaceId) });
  };
  const edit = useMutation({
    mutationFn: (body: unknown) => editMessage(workspaceId, message.requestId, message.id, body),
    onSuccess: () => {
      setMode('read');
      refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => deleteMessage(workspaceId, message.requestId, message.id),
    onSuccess: refresh,
  });

  const who = message.authorName || 'Unknown';
  const menu: MenuAction[] = [
    ...(mayChange
      ? [
          { key: 'edit', label: 'Edit message', icon: Pencil, onSelect: () => setMode('edit') },
          {
            key: 'delete',
            label: 'Delete message',
            icon: Trash2,
            danger: true,
            onSelect: () => setMode('confirm-delete'),
          },
        ]
      : []),
    {
      key: 'copy',
      label: 'Copy text',
      icon: Copy,
      separated: mayChange,
      onSelect: () => void navigator.clipboard?.writeText(message.bodyText),
    },
  ];
  const inlineInBody = inlineAttachmentIds(message.body);
  return (
    <ActionContextMenu actions={menu}>
      <div className="group flex gap-2.5">
        <div
          className={cx(
            'grid size-[1.625rem] flex-none place-items-center rounded-full fs-11 font-bold text-white',
            avatarColour(who),
          )}
          aria-hidden
        >
          {initialOf(who)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="flex-none fs-12 font-bold whitespace-nowrap">{who}</span>
            <span className="fs-11 whitespace-nowrap text-muted">{formatDateTime(message.createdAt)}</span>
            <EditedMark at={message.editedAt} />
            {/* Only ever present on the vendor projection — the server does not
              send internal messages to a client at all. */}
            {!message.visibleToClient && (
              <span className="rounded-full bg-violet-tint px-1.5 fs-10 font-semibold text-violet">
                Internal
              </span>
            )}
            {mode === 'read' && (
              <MessageActions
                canEdit={mayChange}
                canDelete={mayChange}
                onEdit={() => setMode('edit')}
                onDelete={() => setMode('confirm-delete')}
              />
            )}
          </div>
          {mode === 'edit' && (
            <MessageEditor
              workspaceId={workspaceId}
              initial={message.body}
              pending={edit.isPending}
              error={edit.error}
              onSave={(body) => edit.mutate(body)}
              onCancel={() => setMode('read')}
            />
          )}
          {mode === 'confirm-delete' && (
            <ConfirmDelete
              prompt="Delete this message for everyone?"
              pending={remove.isPending}
              error={remove.error}
              onConfirm={() => remove.mutate()}
              onCancel={() => setMode('read')}
            />
          )}
          {/* A message posted with only a file has an empty document; showing an
            empty text block would be noise next to the thumbnail it describes.
            The old check was for a literal '(attachment)' placeholder string —
            gone along with the plain-text `body` column, now that the server
            itself refuses an empty document with nothing attached. */}
          {/* fallbackText carries messages written before the rich composer
            existed: their body is a doc with no content array, so the tree
            walk renders nothing and the whole conversation history would go
            blank without it. */}
          {mode !== 'edit' && message.bodyText.trim() !== '' && (
            <MessageBodyView
              body={message.body}
              fallbackText={message.bodyText}
              resolveAttachmentUrl={resolveAttachmentUrl}
            />
          )}
          {/* Minus anything the body already shows inline. */}
          <AttachmentStrip
            attachments={attachments.filter((item) => !inlineInBody.has(item.id))}
            className="mt-1.5"
          />
        </div>
      </div>
    </ActionContextMenu>
  );
}

export function Conversation({
  workspaceId,
  requestId,
  composerRef,
  opening,
  footer,
  placeholder = 'Write an update, or paste a screenshot. ⌘↵ to post.',
}: {
  workspaceId: string;
  requestId: string;
  /** Owned by the screen so "Ask the client" can put the cursor in here. */
  composerRef: RefObject<ComposerHandle | null>;
  /**
   * Drawn above the first message. A chat thread's opening post is the
   * request's description, not a message, and in a chat there is no
   * description panel beside the thread to show it — so it goes here.
   */
  opening?: ReactNode;
  /** Drawn under the last message — @rundown's "working on it", in a thread. */
  footer?: ReactNode;
  placeholder?: string;
}) {
  const queryClient = useQueryClient();
  const capabilities = useCapabilities();
  const canWrite = capabilities.has('message.write');
  const canBeInternal = capabilities.has('internal.read');

  // The document itself lives inside the Tiptap editor, not in React state —
  // Composer only tells this component whether it is currently empty, which
  // is all the Send button's gate needs. The actual content is pulled
  // imperatively (`composerRef.current.getContent()`) only at send time.
  const [composerEmpty, setComposerEmpty] = useState(true);
  const [internalOnly, setInternalOnly] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);

  const query = useInfiniteQuery(messagesInfiniteQueryOptions(workspaceId, requestId));
  // REVERSED, then flattened. The thread is paged backwards from the newest —
  // page 0 is the tail of the conversation, page 1 the block before it — while
  // each page's own `items` are oldest-first. Flattening the pages in the order
  // TanStack holds them would therefore render the newest block above the older
  // one it came after: no error, no crash, just a conversation that reads out of
  // order in 100-message chunks. `messagesApi.ts` has the full note.
  const messages = (query.data?.pages ?? []).toReversed().flatMap((page) => page.items);

  // Uploaded against the request straight away, then linked to the message when
  // it is posted. A file dropped on the composer is already safe before the
  // reply is written.
  const uploader = useUploader(workspaceId, { requestId });

  const attachmentsQuery = useQuery(attachmentsQueryOptions(workspaceId, requestId));
  // Inline images in a message body store an attachment id — the URL is a
  // presigned GET that expires, so it is resolved here, per render, from the
  // list this screen already loads.
  const resolveAttachmentUrl = (attachmentId: string) =>
    (attachmentsQuery.data?.items ?? []).find((item) => item.id === attachmentId)?.url;
  const byMessage = new Map<string, Attachment[]>();
  for (const attachment of attachmentsQuery.data?.items ?? []) {
    if (attachment.messageId === null) continue;
    byMessage.set(attachment.messageId, [...(byMessage.get(attachment.messageId) ?? []), attachment]);
  }

  const post = useMutation({
    mutationFn: (input: NewMessageInput) => postMessage(workspaceId, requestId, input),
    onSuccess: () => {
      composerRef.current?.clear();
      uploader.clear();
      void queryClient.invalidateQueries({
        queryKey: queryKeys.requestMessages(workspaceId, requestId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.requestAttachments(workspaceId, requestId),
      });
    },
  });

  // Follow the conversation down as it grows, the way a chat does.
  //
  // Keyed on the id of the NEWEST message rather than on `messages.length`,
  // which is what it used to be. Length was fine while the thread could only
  // grow downwards, but "Load earlier messages" grows it upwards: a
  // length-keyed effect would answer every click by throwing the reader back
  // down to the bottom, away from the older messages they had just asked for.
  // The id only changes when something genuinely new arrives at the end, and a
  // refetch returning identical content still moves nothing.
  const newestMessageId = messages.at(-1)?.id;
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [newestMessageId]);

  // A screenshot on its own is a valid reply — "here, look" is the message.
  // The server has the final say on "empty doc, no attachment" (see
  // MessagesService.post) — this is only the UI's gate on the button, not the
  // validation itself.
  const canSend =
    (!composerEmpty || uploader.attachmentIds.length > 0) &&
    !post.isPending &&
    !uploader.isUploading &&
    canWrite;

  const send = () => {
    if (!canSend) return;
    const body = composerRef.current?.getContent();
    if (body === undefined || body === null) return;
    post.mutate({
      body,
      internalOnly: canBeInternal ? internalOnly : undefined,
      attachmentIds: uploader.attachmentIds,
    });
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
        {opening}
        {query.isPending ? (
          <p className="fs-12 text-muted">Loading conversation…</p>
        ) : query.error !== null ? (
          <p className="fs-12 text-danger">{query.error.message}</p>
        ) : messages.length === 0 ? (
          opening === undefined && (
            <p className="fs-12 text-muted">
              No replies yet. Add detail, paste a screenshot, or ask where it sits.
            </p>
          )
        ) : (
          <>
            {/* At the TOP, because that is the direction this one goes: the
                thread opens on its newest messages and "more" means further
                back. Above the first row, where a reader who has scrolled to
                the start of what is loaded is already looking. */}
            <LoadMore
              hasMore={query.hasNextPage}
              isLoading={query.isFetchingNextPage}
              onLoadMore={() => void query.fetchNextPage()}
              label="Load earlier messages"
              className="pb-1"
            />
            {messages.map((message) => (
              <MessageRow
                key={message.id}
                workspaceId={workspaceId}
                message={message}
                attachments={byMessage.get(message.id) ?? []}
                resolveAttachmentUrl={resolveAttachmentUrl}
              />
            ))}
          </>
        )}
        {footer}
        <div ref={endRef} />
      </div>

      {canWrite && (
        <div
          className="flex-none border-t border-line p-3"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => uploader.handleDrop(event)}
        >
          <Composer
            ref={composerRef}
            workspaceId={workspaceId}
            placeholder={placeholder}
            onSubmit={send}
            onEmptyChange={setComposerEmpty}
            onPasteFiles={(event) => uploader.handlePaste(event)}
            onUpload={async (files) => {
              const landed = await uploader.addFiles(files);
              // An inline image resolves its URL from this list.
              await attachmentsQuery.refetch();
              return landed;
            }}
          />
          <PendingStrip uploads={uploader.uploads} onRemove={uploader.remove} />

          <div className="mt-1.5 flex items-center gap-2">
            {canBeInternal && (
              <label className="flex items-center gap-1.5 fs-11 text-muted">
                <input
                  type="checkbox"
                  checked={internalOnly}
                  onChange={(event) => setInternalOnly(event.target.checked)}
                />
                Internal note
              </label>
            )}
            {post.error !== null && <span className="truncate fs-11 text-danger">{post.error.message}</span>}
            <button
              type="button"
              onClick={send}
              disabled={!canSend}
              className="ml-auto rounded-control bg-ink px-3 py-1.5 fs-12 font-semibold whitespace-nowrap text-white disabled:opacity-40"
            >
              {post.isPending ? 'Posting…' : 'Post'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Files on the request itself, as opposed to on one reply.
 *
 * Separate from the conversation's copies because they answer a different
 * question: these are the evidence the request is about — the screenshot of the
 * broken screen — and they should stay visible without scrolling back through
 * a thread to find them.
 */
function RequestAttachments({
  workspaceId,
  requestId,
  inlineIds,
}: {
  workspaceId: string;
  requestId: string;
  /** Shown in the description already — listing them here too would show every
   * pasted screenshot twice, the second time as a file that looks separate. */
  inlineIds: ReadonlySet<string>;
}) {
  const query = useQuery(attachmentsQueryOptions(workspaceId, requestId));
  const items = (query.data?.items ?? []).filter(
    (attachment) => attachment.messageId === null && !inlineIds.has(attachment.id),
  );

  if (items.length === 0) return null;

  return (
    <section>
      <div className="mb-1.5 fs-11 font-bold tracking-[0.06em] text-muted uppercase">Attachments</div>
      <AttachmentStrip attachments={items} />
    </section>
  );
}

/**
 * The right-hand pane: the conversation, and the trail, as two tabs.
 *
 * The trail used to be a collapsed section at the very bottom of the LEFT
 * column, under the time log — the furthest point from anything, reached by
 * scrolling past the description, the fields, the progress bar and every hour
 * ever logged. Its own comment there argued that "the page has no tabs and
 * inventing a tab strip for one occupant would be a bigger change to the
 * screen than the content warrants". That was true when it was the only
 * occupant. It is not the only occupant: the conversation and the trail answer
 * the same question — what has happened on this request — one in people's
 * words and one in the system's, and they belong side by side in the same
 * full-height pane rather than one being a footnote to the other.
 *
 * Activity mounts only while its tab is selected, so the trail fetches nothing
 * for the overwhelmingly common case of opening a request to read and reply.
 */
function ConversationPane({
  workspaceId,
  request,
  composerRef,
  footer,
}: {
  workspaceId: string;
  request: RequestRow;
  composerRef: RefObject<ComposerHandle | null>;
  footer?: ReactNode;
}) {
  const [tab, setTab] = useState<'chat' | 'activity'>('chat');

  return (
    <Tabs.Root
      value={tab}
      onValueChange={(next: string) => setTab(next as 'chat' | 'activity')}
      className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-line"
    >
      <div className="flex flex-none items-end gap-2 border-b border-line px-2 pt-2">
        {/* Radix Tabs, not hand-rolled buttons.
            `role="tablist"` promises a screen-reader user that arrow keys move
            between tabs and Home/End jump to the ends. This markup declared
            the role and delivered none of it, and the app's OTHER hand-rolled
            tab strip (HomePage) declared a different subset of the ARIA again.
            @radix-ui/react-tabs was already a dependency and imported nowhere. */}
        <Tabs.List aria-label="Request" className="flex gap-1">
          {(['chat', 'activity'] as const).map((value) => (
            <Tabs.Trigger
              key={value}
              value={value}
              className={cx(
                'rounded-t-lg px-3 py-1.5 fs-11 font-bold tracking-[0.06em] uppercase',
                'data-[state=active]:bg-surface-2 data-[state=active]:text-ink',
                'text-muted hover:text-ink',
              )}
            >
              {value === 'chat' ? 'Chat' : 'Activity'}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        {/* Whether you hear about this request, beside the thread it is mostly
            about. One bit, said in a word — see FollowToggle. */}
        <FollowToggle workspaceId={workspaceId} requestId={request.id} className="mb-1 ml-auto" />
      </div>

      {/* Both branches unmount the other: the conversation holds an editor and
          a scroll position, and keeping it mounted-but-hidden behind the trail
          would mean its "follow the thread down" effect fires against a pane
          nobody is looking at. */}
      {/* `forceMount` is NOT used, deliberately: each panel unmounts the other.
          The conversation holds an editor and a scroll position, and keeping it
          mounted-but-hidden behind the trail would mean its "follow the thread
          down" effect fires against a pane nobody is looking at. */}
      <Tabs.Content value="chat" className="flex min-h-0 flex-1 flex-col" tabIndex={-1}>
        <Conversation
          workspaceId={workspaceId}
          requestId={request.id}
          composerRef={composerRef}
          footer={footer}
        />
      </Tabs.Content>
      <Tabs.Content value="activity" className="min-h-0 flex-1 overflow-y-auto" tabIndex={-1}>
        <div className="p-4">
          <RequestActivityTab requestId={request.id} />
        </div>
      </Tabs.Content>
    </Tabs.Root>
  );
}

export function RequestDetailPage() {
  const { workspace } = useRequiredWorkspace();
  const { id } = useParams();

  const query = useQuery({
    ...requestQueryOptions(workspace.id, id ?? ''),
    enabled: id !== undefined,
  });

  if (query.isPending) {
    return <p className="p-8 fs-13 text-muted">Loading request…</p>;
  }
  if (query.error !== null) {
    return <EmptyState title="Could not load this request" description={query.error.message} />;
  }
  if (query.data === undefined) {
    return <EmptyState title="Request not found" description="It may have been merged or removed." />;
  }

  return <RequestView request={query.data} />;
}

/**
 * One request, laid out — the whole of this screen below its route, and also
 * what an issue looks like when it is opened from Home's thread list.
 *
 * `inThread` is that second case. The thread sidebar is the way back, so
 * there is no Back link; and @rundown's questions arrive as a card in the
 * conversation, so the intake section above the fields would ask them twice.
 */
export function RequestView({
  request,
  inThread = false,
  conversationFooter,
  actions,
}: {
  request: RequestRow;
  inThread?: boolean;
  conversationFooter?: ReactNode;
  /** Extra controls at the end of the chip row — "Keep as chat", in a thread. */
  actions?: ReactNode;
}) {
  const { workspace } = useRequiredWorkspace();
  const canReview = useCapability('request.review');
  /**
   * `board.write` gates the two delivery acts this screen offers: moving the
   * request through the pipeline, and logging hours against it. One capability,
   * read once — they are the same permission and were always meant to be.
   */
  const canDeliver = useCapability('board.write');
  const userId = useAuth().user?.id ?? null;
  const composerRef = useRef<ComposerHandle>(null);
  const splitRef = useRef<HTMLDivElement>(null);
  const [split, setSplit, commitSplit] = useStoredNumber(
    'rundown.request.detailsSplit',
    SPLIT_DEFAULT,
    clampSplit,
  );

  const internal = isInternalRequest(request) ? request : null;
  const [tab, setTab] = useState<LeftTab>('description');
  const timeEntries = useQuery({
    ...timeEntriesQueryOptions(workspace.id, request.id),
    enabled: canDeliver,
  });
  const loggedHours = (timeEntries.data ?? []).reduce((sum, entry) => sum + entry.minutes, 0) / 60;
  // The same condition ReviewBanner returns null on, so exactly one of the two
  // action rows is ever on screen.
  const showingReviewBanner = canReview && internal !== null && internal.needsReview;

  return (
    /* h-full, not 100dvh: the shell owns the viewport and this fills whatever it
       is given. overflow-hidden here is what makes the two columns below scroll
       rather than the screen. */
    <div className="animate-enter flex h-full min-h-0 flex-1 flex-col overflow-hidden px-5 pt-4 pb-5 md:px-8">
      {inThread ? (
        <Link to="/" className="mb-2.5 flex-none self-start fs-12 text-muted hover:text-ink md:hidden">
          ← Threads
        </Link>
      ) : (
        <Link to="/" className="mb-2.5 flex-none self-start fs-12 text-muted hover:text-ink">
          ← Back
        </Link>
      )}

      <div className="mb-1.5 flex flex-none flex-wrap items-center gap-2">
        <span className="numeric fs-12 text-muted">#{request.number}</span>
        <Chip tone={TYPE_TONES[request.type]}>{TYPE_LABELS[request.type]}</Chip>
        <Chip tone={STATUS_TONES[request.status]}>{STATUS_LABELS[request.status]}</Chip>
        {internal !== null && !internal.visibleToClient && <Chip tone="violet">Internal</Chip>}
        {/* Only when the review banner is not showing: it carries its own
            copy, as the alternatives to Accept, and two sets of the same two
            buttons on one screen is a question about which one is which. */}
        {!showingReviewBanner && <RequestActions workspaceId={workspace.id} request={request} />}
        {/* RequestActions pushes itself right; with it absent (the review
            banner carries its own), these have to. */}
        {actions !== undefined && (
          <span className={cx('flex-none', showingReviewBanner && 'ml-auto')}>{actions}</span>
        )}
      </div>

      {/* Said at the top, before the title: a merged request is Done and its
          thread has stopped, and without this the screen gives no reason why. */}
      {request.duplicateOf !== null && (
        <div className="mb-2 flex flex-none flex-wrap items-baseline gap-1.5 rounded-[10px] border border-line bg-surface-2 px-3 py-2 fs-12 text-muted">
          <span className="font-semibold text-ink">Merged as a duplicate.</span>
          <span>The work is tracked on</span>
          <Link
            to={`/requests/${request.duplicateOf.requestId}`}
            className="font-semibold text-accent hover:underline"
          >
            #{request.duplicateOf.number} {request.duplicateOf.title}
          </Link>
        </div>
      )}

      <TitleBlock workspaceId={workspace.id} request={request} canEdit={canReview} />

      <div className="mt-1 mb-3 flex-none fs-11.5 text-muted">
        Raised by {request.requesterName ?? 'Unknown'} · {formatDateTime(request.createdAt)}
      </div>

      {internal !== null && (
        <ReviewBanner workspaceId={workspace.id} request={internal} onEditFields={() => setTab('details')} />
      )}

      <RequestSummary request={request} loggedHours={canDeliver ? loggedHours : null} onOpen={setTab} />

      {/* minmax(0,…) on both tracks, not 1fr/1.15fr alone: a grid track sized
          `auto`/`1fr` still refuses to go below its content's min-content width,
          so a long unbroken line would push the layout wider than the screen.
          Three tracks from lg up — the request, the divider, the conversation —
          and one column below it, where the divider is not rendered at all. The
          split is a percentage of this grid, remembered in this browser. */}
      <div
        ref={splitRef}
        style={{ '--details-w': `${split}%` } as CSSProperties}
        className="grid min-h-0 flex-1 gap-7 lg:grid-cols-[minmax(0,var(--details-w))_auto_minmax(0,1fr)] lg:gap-0"
      >
        {/* The request itself, in three tabs. Description first and on its
            own: it is what people read, and it reads best with nothing
            beside it. Everything that is set rather than read — the fields,
            the delivery column, hours — is one tab away, and the summary above
            says what those tabs hold without opening them. */}
        <Tabs.Root
          value={tab}
          onValueChange={(next: string) => setTab(next as LeftTab)}
          className="flex min-h-0 min-w-0 flex-col lg:pr-3"
        >
          <Tabs.List aria-label="Request" className="flex flex-none gap-4 border-b border-surface-3">
            {(
              [
                ['description', 'Description'],
                ['details', 'Details'],
                ...(canDeliver ? [['time', 'Time'] as const] : []),
              ] as const
            ).map(([value, label]) => (
              <Tabs.Trigger
                key={value}
                value={value}
                className={cx(
                  '-mb-px flex items-center gap-1.5 border-b-2 border-transparent pb-2 fs-13 text-muted hover:text-ink',
                  'data-[state=active]:border-ink data-[state=active]:font-semibold data-[state=active]:text-ink',
                )}
              >
                {label}
                {value === 'time' && loggedHours > 0 && (
                  <span className="numeric fs-11 text-faint">{formatHours(loggedHours)}</span>
                )}
              </Tabs.Trigger>
            ))}
          </Tabs.List>

          <Tabs.Content
            value="description"
            tabIndex={-1}
            className="min-h-0 flex-1 space-y-6 overflow-y-auto pt-4 pr-1 pb-10"
          >
            {/* While intake is still asking, its questions come first — the
                description is provisional until they are answered. Renders
                nothing once the conversation is over, which is nearly always. */}
            {!inThread && <RequestIntakeSection workspaceId={workspace.id} requestId={request.id} />}

            {/* The person who raised it keeps editing their own words — that is
                not a review capability and the server checks it against the row,
                not against the role. Matching on the id, never the display name. */}
            <Description
              workspaceId={workspace.id}
              request={request}
              canEdit={canReview || (request.requesterId !== null && request.requesterId === userId)}
              bare
            />

            <RequestAttachments
              workspaceId={workspace.id}
              requestId={request.id}
              inlineIds={inlineAttachmentIds(request.description)}
            />
          </Tabs.Content>

          <Tabs.Content
            value="details"
            tabIndex={-1}
            className="min-h-0 flex-1 space-y-5 overflow-y-auto pt-4 pr-1 pb-10"
          >
            <section>
              <div className="mb-2 flex items-baseline justify-between gap-2.5">
                <span
                  className={cx(
                    'fs-12.5 font-semibold whitespace-nowrap',
                    TONE_TEXT[STATUS_TONES[request.status]] ?? 'text-muted',
                  )}
                >
                  {STATUS_LABELS[request.status]}
                </span>
                <span className="fs-11 whitespace-nowrap text-muted">
                  Step {STATUS_ORDER.indexOf(request.status) + 1} of {STATUS_ORDER.length}
                </span>
              </div>
              <Progress status={request.status} />
            </section>

            {internal !== null && canDeliver && (
              <div className="grid grid-cols-2 gap-3">
                <DeliveryColumn
                  workspaceId={workspace.id}
                  requestId={request.id}
                  column={internal.boardColumn}
                />
              </div>
            )}

            <RequestFields workspaceId={workspace.id} request={request} embedded />
          </Tabs.Content>

          {canDeliver && (
            <Tabs.Content
              value="time"
              tabIndex={-1}
              className="min-h-0 flex-1 overflow-y-auto pt-4 pr-1 pb-10"
            >
              <RequestTimeLog workspaceId={workspace.id} requestId={request.id} />
            </Tabs.Content>
          )}
        </Tabs.Root>

        <ResizeHandle
          label="Resize details and conversation"
          value={split}
          min={SPLIT_MIN}
          max={SPLIT_MAX}
          step={2}
          pxToValue={(px) => (px / (splitRef.current?.clientWidth ?? 1)) * 100}
          onChange={setSplit}
          onCommit={commitSplit}
          onReset={() => commitSplit(SPLIT_DEFAULT)}
          className="hidden w-4 lg:block"
        />

        <ConversationPane
          workspaceId={workspace.id}
          request={request}
          composerRef={composerRef}
          footer={conversationFooter}
        />
      </div>
    </div>
  );
}
